import type { Layer } from "@geosoft/viewer-sdk";

function layerCoordinateMode(layer: Layer): string {
  return typeof layer.style?.coordinateMode === "string"
    ? layer.style.coordinateMode
    : "georeferenced";
}

export function hasExplicitGeospatialBounds(layer: Layer): boolean {
  if (layer.style?.hasExplicitBounds === false) return false;
  if (!layer.bbox || layer.bbox.length !== 4) return false;
  if (!layer.bbox.every((value) => Number.isFinite(value))) return false;
  return layerCoordinateMode(layer) === "georeferenced";
}

/** Geographic lon/lat bbox suitable for `flyToBbox(..., 'EPSG:4326')`. */
export function isFlyableGeographicBbox(
  bbox: readonly number[] | undefined | null,
): boolean {
  if (!bbox || bbox.length < 4) return false;
  if (!bbox.every((v) => Number.isFinite(v))) return false;
  const [minX, minY, maxX, maxY] = bbox;
  if (!(maxX > minX && maxY > minY)) return false;
  if (
    Math.abs(minX) > 180 ||
    Math.abs(maxX) > 180 ||
    Math.abs(minY) > 90 ||
    Math.abs(maxY) > 90
  ) {
    return false;
  }
  const lonSpan = Math.abs(maxX - minX);
  const latSpan = Math.abs(maxY - minY);
  if (lonSpan >= 359 || latSpan >= 169) return false;
  return true;
}

function isBasemapLayer(layer: Layer): boolean {
  return layer.kind === "raster" && layer.id.includes("basemap");
}

/**
 * Union of flyable WGS84 bboxes across registered catalog layers.
 * Used when no layers are visible (on-demand all hidden) so the camera
 * frames project extent instead of null island.
 */
export function unionFlyableGeographicBboxes(
  layers: readonly Layer[],
): [number, number, number, number] | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let count = 0;

  for (const layer of layers) {
    if (!hasExplicitGeospatialBounds(layer)) continue;
    if (isBasemapLayer(layer)) continue;
    if (!isFlyableGeographicBbox(layer.bbox)) continue;
    const [x0, y0, x1, y1] = layer.bbox!;
    minX = Math.min(minX, x0, x1);
    minY = Math.min(minY, y0, y1);
    maxX = Math.max(maxX, x0, x1);
    maxY = Math.max(maxY, y0, y1);
    count += 1;
  }

  if (count === 0) return null;
  return [minX, minY, maxX, maxY];
}
